import {Placement} from '@popperjs/core';
import {OffsetModifier} from '@popperjs/core/lib/modifiers/offset.d';
import classNames from 'classnames';
import React, {ReactNode, useEffect, useState} from 'react';
import TooltipTrigger, {TriggerTypes} from 'react-popper-tooltip';
import {arrow, flip, offset, preventOverflow} from './CLOverlayModifiers';
import iconsObj from 'common/src/assets/icons/iconsObj';
import CLMaskIcon from 'common/src/components/CLMaskIcon/CLMaskIcon';
import useEvent from 'common/src/hooks/useEvent';
import styles from './CLOverlayTrigger.module.scss';
import {AutomationProps} from 'common/src/modules/automation/automation.types';
import addAutomationTag from 'common/src/modules/automation/utils/addAutomationTag';
//https://reactjsexample.com/react-tooltip-library-built-around-react-popper/
//https://atomiks.github.io/tippyjs/

export enum PopperType {
  Tooltip = 'tooltip',
  Popover = 'popover',
  Warning = 'warning',
  Error = 'error',
  Menu = 'menu',
  Calendar = 'calendar',
  Issues = 'issues',
  Settings = 'settings',
}

export enum PinPosition {
  Right = 'right',
  Left = 'left',
  Middle = 'middle',
}

export type WithPinProps = {
  //include the ability to pin the tooltip
  showPin?: boolean;
  pinPosition?: PinPosition;
};

export type WithoutPinProps = {
  showPin?: false;
  pinPosition?: never;
};

export type CLOverlayTriggerProps = AutomationProps &
  (WithPinProps | WithoutPinProps) & {
    placement?: Placement;
    style?: React.CSSProperties;
    arrowStyle?: React.CSSProperties;
    trigger: TriggerTypes;
    popper: ReactNode;
    tooltipShown?: boolean;
    type?: PopperType;
    component?: ReactNode;
    triggerClassName?: string;
    tooltipBodyClassName?: string;
    tooltipContainerClassName?: string;
    flips?: Placement[];
    delayShow?: number;
    delayHide?: number;
    children?: ReactNode;
    usePortal?: boolean;
    popperOffset?: Partial<OffsetModifier>;
    onVisibilityChange?: (isVisible: boolean) => void;
    closeOnReferenceHidden?: boolean;
    defaultTooltipShown?: boolean;
    fitContent?: boolean;
    tooltipBodyStyle?: React.CSSProperties;
    boundaryPadding?: number;
  };

const CLOverlayTrigger = ({
  placement,
  trigger,
  tooltipShown,
  popper,
  type = PopperType.Tooltip,
  component,
  style,
  arrowStyle,
  triggerClassName = '',
  tooltipBodyClassName = '',
  tooltipContainerClassName = '',
  fitContent = true,
  flips,
  delayShow,
  delayHide,
  children,
  defaultTooltipShown = false,
  usePortal = true,
  popperOffset = offset as Partial<OffsetModifier>,
  onVisibilityChange = () => {},
  tooltipBodyStyle,
  closeOnReferenceHidden = false,
  boundaryPadding = 0,
  showPin = false,
  pinPosition = PinPosition.Right,
  qaHook = '',
}: CLOverlayTriggerProps) => {
  const [managedShowState, setManagedShowState] = useState(tooltipShown);
  const [isPinActive, setIsPinActive] = useState(false);

  const modifiers = [
    {...preventOverflow, options: {...preventOverflow.options, padding: boundaryPadding}},
    popperOffset,
    arrow,
    {...flip, options: {...flip.options, fallbackPlacements: flips}},
  ];

  const typeClassMap: Record<PopperType, string> = {
    tooltip: styles['tooltip-element'],
    popover: styles['popover-element'],
    warning: styles['warning-tooltip-element'],
    error: styles['error-tooltip-element'],
    menu: styles['menu-element'],
    calendar: styles['calendar-element'],
    issues: styles['issues-element'],
    settings: styles['settings-element'],
  };

  useEffect(
    function manageShowStateFromOutside() {
      if (showPin) return;

      setManagedShowState(tooltipShown);
    },
    [tooltipShown, showPin],
  );

  const containerClassName = typeClassMap[type];
  const togglePinState = useEvent(() => {
    setIsPinActive(!isPinActive);
    setManagedShowState(managedShowState ? undefined : true);
  });

  if (!popper)
    return (
      <span
        className={classNames(triggerClassName, {[styles.cl_common_tooltip_trigger]: fitContent})}
      >
        {children}
      </span>
    );

  return (
    <TooltipTrigger
      onVisibilityChange={onVisibilityChange}
      placement={placement || 'right'}
      trigger={trigger}
      tooltipShown={managedShowState ?? tooltipShown}
      modifiers={modifiers}
      usePortal={usePortal}
      portalContainer={document.body}
      defaultTooltipShown={defaultTooltipShown}
      delayShow={delayShow}
      delayHide={delayHide}
      closeOnReferenceHidden={closeOnReferenceHidden}
      tooltip={({getTooltipProps, getArrowProps, tooltipRef, arrowRef, placement}) => (
        <div
          {...addAutomationTag(qaHook)}
          {...getTooltipProps({
            ref: tooltipRef,
            className: classNames(
              styles.common_cl_overlay_trigger_popper_container,
              containerClassName,
              tooltipContainerClassName,
            ),
          })}
        >
          {showPin && (
            <div>
              <div
                className={classNames(
                  styles.pin_icon_container,
                  {[styles.pin_active]: isPinActive},
                  styles[pinPosition],
                )}
              >
                <CLMaskIcon
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    togglePinState();
                  }}
                  src={iconsObj.pin}
                  className={styles.pin_icon}
                  bgColor="white"
                />
              </div>
            </div>
          )}

          {type !== 'menu' && (
            <div
              {...getArrowProps({
                ref: arrowRef,
                'data-placement': placement,
                className: styles['tooltip-arrow'],
                style: arrowStyle,
              })}
            />
          )}
          <div
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            className={classNames(styles.cl_common_tooltip_body, tooltipBodyClassName)}
            style={tooltipBodyStyle}
          >
            {popper}
          </div>
        </div>
      )}
    >
      {({getTriggerProps, triggerRef}) => (
        <span
          {...getTriggerProps({
            ref: triggerRef,
            className: classNames(triggerClassName, {cl_common_tooltip_trigger: fitContent}),
          })}
          style={style}
        >
          {component || children}
        </span>
      )}
    </TooltipTrigger>
  );
};

export default CLOverlayTrigger;
